// ============================================================================
// Routes Metadata Configuration
// ============================================================================

// Use relative imports for Node.js compatibility (static generation script)
// eslint-disable-next-line no-restricted-imports
import { COMPANY_DETAILS_TABS, COMPANY_EDIT_STEPS } from '../services/consts/company.const.js'
// eslint-disable-next-line no-restricted-imports
import { ROUTES } from '../services/consts/routes.const.js'

// ============================================================================
// Constants
// ============================================================================

export const THUMBNAIL_PATH = '/thumbnail.png'

export const DEFAULT_META = {
  title: 'Armadillex | Companies Database',
  description:
    'Manage and organize your companies database. View company profiles, contact information, data protection frameworks, AI assessments, and product details. Track compliance status and manage company relationships.',
}

// ============================================================================
// Main Configuration Object
// ============================================================================

export const ROUTES_METADATA = {
  [ROUTES.APP]: {
    meta: DEFAULT_META,
  },

  [ROUTES.LOGIN]: {
    staticPath: '/login',
    meta: {
      title: 'Login - Armadillex | Companies Database',
      description: 'Sign in to access your Armadillex Companies Database account',
    },
  },

  [ROUTES.COMPANY]: {
    staticPath: '/company',
    meta: {
      title: 'Companies',
      description:
        'Browse, search and filter companies in your database. View company profiles, filter by status, AI services, data protection frameworks, and more.',
    },
  },

  [ROUTES.CREATION_REQUESTS]: {
    staticPath: '/creation-requests',
    meta: {
      title: 'Creation Requests',
      description:
        'Review and manage company creation requests. Approve or deny subprocessor creation requests.',
    },
  },

  [ROUTES.COMPANY_INFO]: {
    staticPath: '/company/:id/:tab?',
    meta: {
      title: 'Company details',
      description:
        'View and manage company information, contact details, data protection frameworks, AI assessments, and products.',
    },
    skipAutoMetaGeneration: true, // Needs company data
    subpages: [
      {
        param: COMPANY_DETAILS_TABS.INFO,
        staticPath: `/company/:id/${COMPANY_DETAILS_TABS.INFO}`,
        title: 'Company information',
        description:
          'View and manage company information including business details, legal information, company status, website, employee count, and organizational structure.',
      },
      {
        param: COMPANY_DETAILS_TABS.CONTACT,
        staticPath: `/company/:id/${COMPANY_DETAILS_TABS.CONTACT}`,
        title: 'Contact information',
        description:
          'View and manage company contact information including addresses, phone numbers, email addresses, and breach notification contacts.',
      },
      {
        param: COMPANY_DETAILS_TABS.DPF,
        staticPath: `/company/:id/${COMPANY_DETAILS_TABS.DPF}`,
        title: 'Data protection framework',
        description:
          'View company Data Protection Framework (DPF) certification status, compliance information, and framework details.',
      },
      {
        param: COMPANY_DETAILS_TABS.AI_ASSESSMENT,
        staticPath: `/company/:id/${COMPANY_DETAILS_TABS.AI_ASSESSMENT}`,
        title: 'Company AI Assessment',
        description:
          'View company AI assessment results, evaluation status, assessment questions and answers, and compliance details.',
      },
      {
        param: COMPANY_DETAILS_TABS.PRODUCTS,
        staticPath: `/company/:id/${COMPANY_DETAILS_TABS.PRODUCTS}`,
        title: 'Products',
        description:
          'View and manage company products, including AI usage information, product assessments, risk levels, and product details.',
      },
      {
        param: COMPANY_DETAILS_TABS.DOCUMENTS,
        staticPath: `/company/:id/${COMPANY_DETAILS_TABS.DOCUMENTS}`,
        title: 'Documents',
        description:
          'View compliance documents attached to this company, including subprocessor lists, privacy policies, audit reports, and related files.',
      },
      {
        param: COMPANY_DETAILS_TABS.SUBPROCESSOR_MAP,
        staticPath: `/company/:id/${COMPANY_DETAILS_TABS.SUBPROCESSOR_MAP}`,
        title: 'Subprocessor map',
        description:
          'View the subprocessor mapping graph showing company relationships, data processing chains, and service provider connections.',
      },
      {
        param: COMPANY_DETAILS_TABS.SUBPROCESSOR_LIST,
        staticPath: `/company/:id/${COMPANY_DETAILS_TABS.SUBPROCESSOR_LIST}`,
        title: 'Subprocessor list',
        description:
          'Browse the full subprocessor list for this company, including source metadata, last update, locations, and service categories. Add or edit individual subprocessors.',
      },
      {
        param: COMPANY_DETAILS_TABS.SUBPROCESSOR_REVERSE,
        staticPath: `/company/:id/${COMPANY_DETAILS_TABS.SUBPROCESSOR_REVERSE}`,
        title: 'Used as a subprocessor',
        description: 'View companies that list this company as an active subprocessor.',
      },
      {
        // Product subpage: same path as products but matches URLs with ?productId query param
        param: 'product',
        staticPath: `/company/:id/${COMPANY_DETAILS_TABS.PRODUCTS}`,
        title: 'Product',
        description:
          'View product details, AI usage information, product assessments, risk levels, and compliance status.',
      },
    ],
    getDynamicMeta: (route, dynamicData) => {
      const companyName = dynamicData?.companyName
      const tab = route.params?.tab || COMPANY_DETAILS_TABS.INFO

      // Check if viewing a specific product
      if (route.query?.productId && dynamicData?.productName) {
        return {
          title: dynamicData?.companyName
            ? `${dynamicData.productName} - ${dynamicData.companyName}`
            : dynamicData.productName,
          description:
            'View product details, AI usage information, product assessments, risk levels, and compliance status.',
        }
      }

      // Get subpage metadata from config
      const config = ROUTES_METADATA[route.name]
      const subpage = config.subpages.find((v) => v.param === tab) || config.subpages[0]

      return {
        title: companyName ? `${companyName} - ${subpage.title}` : subpage.title,
        description: subpage.description,
      }
    },
  },

  [ROUTES.COMPANY_EDIT]: {
    staticPath: '/company/:id/edit/:step?',
    meta: {
      title: 'Edit company',
      description:
        'Edit company information, contact details, data protection frameworks, AI assessments, and products.',
    },
    skipAutoMetaGeneration: true, // Needs company data
    subpages: [
      {
        param: COMPANY_EDIT_STEPS.INFO,
        staticPath: `/company/:id/edit/${COMPANY_EDIT_STEPS.INFO}`,
        title: 'Edit Company information',
        description:
          'Edit company information including business details, legal information, company status, website, employee count, and organizational structure.',
      },
      {
        param: COMPANY_EDIT_STEPS.PARENT,
        staticPath: `/company/:id/edit/${COMPANY_EDIT_STEPS.PARENT}`,
        title: 'Edit Parent company',
        description:
          'Edit parent company information, organizational structure, and company relationships.',
      },
      {
        param: COMPANY_EDIT_STEPS.CONTACT,
        staticPath: `/company/:id/edit/${COMPANY_EDIT_STEPS.CONTACT}`,
        title: 'Edit Contact information',
        description:
          'Edit company contact information including addresses, phone numbers, email addresses, and breach notification contacts.',
      },
      {
        param: COMPANY_EDIT_STEPS.AI_ASSESSMENT,
        staticPath: `/company/:id/edit/${COMPANY_EDIT_STEPS.AI_ASSESSMENT}`,
        title: 'Edit Company AI Assessment',
        description:
          'Edit company AI assessment details, questions, answers, evaluation status, and compliance information.',
      },
      {
        param: COMPANY_EDIT_STEPS.PRODUCTS,
        staticPath: `/company/:id/edit/${COMPANY_EDIT_STEPS.PRODUCTS}`,
        title: 'Edit Products',
        description:
          'Edit company products, including AI usage information, product assessments, risk levels, and product details.',
      },
      {
        // Product subpage: same path as products but matches URLs with ?productId query param
        param: COMPANY_EDIT_STEPS.PRODUCTS,
        staticPath: `/company/:id/edit/${COMPANY_EDIT_STEPS.PRODUCTS}`,
        title: 'Edit Product',
        description:
          'Edit product details, AI usage information, product assessments, risk levels, and compliance status.',
      },
      {
        param: COMPANY_EDIT_STEPS.SUBPROCESSORS,
        staticPath: `/company/:id/edit/${COMPANY_EDIT_STEPS.SUBPROCESSORS}`,
        title: 'Edit Subprocessors',
        description:
          'Edit company subprocessors, including category, service details, locations, and legal entity information.',
      },
      {
        // Subprocessor subpage: same path as subprocessors but matches URLs with ?subprocessorId query param
        param: COMPANY_EDIT_STEPS.SUBPROCESSORS,
        staticPath: `/company/:id/edit/${COMPANY_EDIT_STEPS.SUBPROCESSORS}`,
        title: 'Edit Subprocessor',
        description:
          'Edit subprocessor details, category, service categories, locations, and legal entity information.',
      },
    ],
    getDynamicMeta: (route, dynamicData) => {
      const companyName = dynamicData?.companyName
      const step = route.params?.step || COMPANY_EDIT_STEPS.INFO

      // Check if editing a specific subprocessor
      if (route.query?.subprocessorId && dynamicData?.subprocessorName) {
        return {
          title: dynamicData?.companyName
            ? `Edit ${dynamicData.subprocessorName} - ${dynamicData.companyName}`
            : `Edit ${dynamicData.subprocessorName}`,
          description:
            'Edit subprocessor details, category, service categories, locations, and legal entity information.',
        }
      }

      // Check if editing a specific product
      if (route.query?.productId && dynamicData?.productName) {
        return {
          title: dynamicData?.companyName
            ? `Edit ${dynamicData.productName} - ${dynamicData.companyName}`
            : `Edit ${dynamicData.productName}`,
          description:
            'Edit product details, AI usage information, product assessments, risk levels, and compliance status.',
        }
      }

      // Get subpage metadata from config
      const config = ROUTES_METADATA[route.name]
      const subpage = config.subpages.find((v) => v.param === step) || config.subpages[0]

      return {
        title: companyName ? `${companyName} - ${subpage.title}` : subpage.title,
        description: subpage.description,
      }
    },
  },
}
